import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Lock, Pencil, Users, X } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '../components';
import { TeamJaarverslagPaneel, teamJaarKey } from '../components/jaarverslag/TeamJaarverslagPaneel';
import { getTeamJaar, zetVerificatie } from '../services/teamJaarverslagApi';
import {
  corrigeer,
  getAudit,
  getContext,
  getStructuur,
  voegSamen,
  zetUitsluiting,
  type Analytics,
  type CodeLabel,
  type Correctie,
  type Registratie,
  type Structuur,
  type Telling,
} from '../services/caseRegistratieApi';

/**
 * Case-registratie & Jaarverslag (migratie 167) voor sjabloon A en B.
 *  - Registreren gebeurt via het gespreksverslag (migratie 176). Een vervolggesprek
 *    hangt aan een bestaande case en telt niet dubbel in de incidentgrafieken.
 *  - Controleren & releasen: de professional ziet en corrigeert de registraties
 *    van het jaar en de preview van wat de werkgever krijgt; pas na de release
 *    verschijnt het jaarverslag bij de werkgever.
 * Geslacht en leeftijd vraagt de professional nooit: die komen uitsluitend uit
 * de vrijwillige, losgekoppelde zelfopgave van de medewerker.
 * Het jaarverslag gaat altijd via het tabblad "Team" (BMW sinds migratie 185,
 * VTP sinds 190): ieder verifieert zijn eigen data, het team schrijft de
 * hoofdstukken en de coördinator geeft één verslag vrij — ook als je de enige
 * bent die voor deze opdrachtgever registreert.
 */
const foutmelding = (e: any) => toast.error(e?.response?.data?.message || 'Er ging iets mis');
const datumLabel = (s: string) => new Date(s).toLocaleDateString('nl-NL');
const labelVan = (lijst: CodeLabel[], code: string | null) => lijst.find((c) => c.code === code)?.label ?? code ?? '—';
const wissel = (lijst: string[], code: string) => (lijst.includes(code) ? lijst.filter((c) => c !== code) : [...lijst, code]);

const CheckboxLijst: React.FC<{ opties: CodeLabel[]; waarde: string[]; onChange: (v: string[]) => void; legend: string }> = ({ opties, waarde, onChange, legend }) => (
  <fieldset>
    <legend className="text-sm font-medium text-gray-800 mb-1">{legend}</legend>
    <div className="space-y-1.5">
      {opties.map((o) => (
        <label key={o.code} className="flex items-start gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="mt-0.5 rounded border-gray-300" checked={waarde.includes(o.code)} onChange={() => onChange(wissel(waarde, o.code))} />
          <span>
            <span className="text-gray-900">{o.label}</span>
            <span className="block text-xs text-gray-500">{o.omschrijving}</span>
          </span>
        </label>
      ))}
    </div>
  </fieldset>
);

const RolKeuze: React.FC<{ opties: CodeLabel[]; waarde: string | null; onChange: (v: string) => void }> = ({ opties, waarde, onChange }) => {
  const groepen = Array.from(new Set(opties.map((o) => o.groep ?? '')));
  return (
    <fieldset>
      <legend className="text-sm font-medium text-gray-800 mb-1">Stap 1B · Rolverhouding melder ➔ veroorzaker</legend>
      {groepen.map((g) => (
        <div key={g} className="mb-2">
          <p className="text-xs uppercase tracking-wide text-gray-500">{g}</p>
          {opties.filter((o) => (o.groep ?? '') === g).map((o) => (
            <label key={o.code} className="flex items-start gap-2 text-sm cursor-pointer mt-1">
              <input type="radio" name="rolverhouding" className="mt-0.5" checked={waarde === o.code} onChange={() => onChange(o.code)} />
              <span>
                <span className="text-gray-900">{o.label}</span>
                <span className="block text-xs text-gray-500">{o.omschrijving}</span>
              </span>
            </label>
          ))}
        </div>
      ))}
    </fieldset>
  );
};

/** VTP stap 1C (migratie 190): optioneel, telt mee in H4.5 'Geslacht beschuldigden'. */
const GeslachtVeroorzakerKeuze: React.FC<{ opties: CodeLabel[]; waarde: string | null; onChange: (v: string | null) => void }> = ({ opties, waarde, onChange }) => (
  <fieldset>
    <legend className="text-sm font-medium text-gray-800 mb-1">Geslacht beschuldigde / lastveroorzaker <span className="font-normal text-gray-500">(optioneel)</span></legend>
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {opties.map((o) => (
        <label key={o.code} className="flex items-center gap-1.5 text-sm cursor-pointer" title={o.omschrijving || undefined}>
          <input type="radio" name="geslacht_veroorzaker" checked={waarde === o.code} onChange={() => onChange(o.code)} />
          {o.label}
        </label>
      ))}
      {waarde && (
        <button type="button" className="text-xs text-gray-500 underline" onClick={() => onChange(null)}>
          Wissen
        </button>
      )}
    </div>
  </fieldset>
);

/** Getrapte dropdown: alleen afdelingen van de gekozen vestiging (plus universele afdelingen). */
const AfdelingKeuze: React.FC<{
  structuur: Structuur | undefined;
  locatie: string;
  afdeling: string;
  onChange: (locatie: string, afdeling: string) => void;
}> = ({ structuur, locatie, afdeling, onChange }) => {
  const afdelingen = (structuur?.afdelingen ?? []).filter((a) => !a.location_id || a.location_id === locatie);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <label className="block text-sm">
        <span className="text-gray-700">Locatie / vestiging</span>
        <select className="mt-1 w-full rounded-md border-gray-300" value={locatie} onChange={(e) => onChange(e.target.value, '')}>
          <option value="">Niet bekend</option>
          {(structuur?.locaties ?? []).map((l) => <option key={l.id} value={l.id}>{l.naam}</option>)}
        </select>
      </label>
      <label className="block text-sm">
        <span className="text-gray-700">Afdeling</span>
        <select className="mt-1 w-full rounded-md border-gray-300" value={afdeling} onChange={(e) => onChange(locatie, e.target.value)}>
          <option value="">Niet bekend</option>
          {afdelingen.map((a) => <option key={a.id} value={a.id}>{a.naam}</option>)}
        </select>
      </label>
    </div>
  );
};

const Staven: React.FC<{ titel: string; tellingen: Telling[] | null; noot?: string | null }> = ({ titel, tellingen, noot }) => {
  const max = Math.max(0, ...(tellingen ?? []).map((t) => t.aantal));
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-900 mb-1">{titel}</h4>
      {!tellingen || max === 0 ? (
        <p className="text-xs text-gray-500 italic">{noot || 'Geen gegevens.'}</p>
      ) : (
        <ul className="space-y-1">
          {tellingen.map((t) => (
            <li key={t.code} className="grid grid-cols-[minmax(0,10rem)_1fr_2rem] items-center gap-2 text-xs">
              <span className="truncate text-gray-700" title={t.label}>{t.label}</span>
              <span className="h-2.5 rounded bg-gray-100">
                <span className={`block h-full rounded ${t.code === 'overig' || t.code === 'onbekend' ? 'bg-gray-300' : 'bg-[#0B1F3A]'}`} style={{ width: `${(t.aantal / max) * 100}%` }} />
              </span>
              <span className="text-right tabular-nums text-gray-900">{t.aantal}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Preview: React.FC<{ p: Analytics }> = ({ p }) =>
  p.verborgen ? (
    <p className="text-sm text-gray-600 italic">{p.verborgen}</p>
  ) : (
    <>
      <Staven titel="Aard van de meldingen*" tellingen={p.aard.verdeling} />
      <p className="text-[11px] text-gray-500">* {p.aard.noot}</p>
      {p.rolverhouding && <Staven titel="Rolverhouding" tellingen={p.rolverhouding.verdeling} />}
      {p.geslacht_veroorzaker && <Staven titel="Geslacht beschuldigden" tellingen={p.geslacht_veroorzaker.verdeling} />}
      <Staven titel="Casussen per afdeling" tellingen={p.afdelingen.groepen.map((g, i) => ({ code: String(i), label: g.label, aantal: g.aantal_casussen }))} />
      <Staven titel={`Geslacht (${p.demografie.aantal_opgaven} opgaven)`} tellingen={p.demografie.geslacht} noot={p.demografie.reden_verborgen} />
      <Staven titel="Leeftijd" tellingen={p.demografie.leeftijd} noot={p.demografie.reden_verborgen} />
      <Staven titel="Interventies (cumulatief per casus)" tellingen={p.interventies} />
    </>
  );

const ProfessionalJaarverslagPage: React.FC = () => {
  const qc = useQueryClient();
  const contextQuery = useQuery({ queryKey: ['case-registratie-context'], queryFn: getContext, retry: false });
  const ctx = contextQuery.data;
  const [employerId, setEmployerId] = useState('');
  const [jaar, setJaar] = useState(new Date().getFullYear());
  const [tab, setTab] = useState<'registreren' | 'audit' | 'team'>('registreren');
  const [bewerk, setBewerk] = useState<{ id: string; waarde: Correctie & { is_nieuw_incident: boolean } } | null>(null);
  const [samenDoel, setSamenDoel] = useState('');
  const [uitsluitReden, setUitsluitReden] = useState('');

  useEffect(() => {
    if (!employerId && ctx?.opdrachtgevers.length === 1) setEmployerId(ctx.opdrachtgevers[0].id);
  }, [ctx, employerId]);

  const { data: structuur } = useQuery({ queryKey: ['case-structuur', employerId], queryFn: () => getStructuur(employerId), enabled: !!employerId && !!ctx });
  const auditKey = ['case-audit', employerId, jaar];
  const auditQuery = useQuery({ queryKey: auditKey, queryFn: () => getAudit(employerId, jaar), enabled: !!employerId && !!ctx });
  const audit = auditQuery.data;
  const verversen = () => {
    qc.invalidateQueries({ queryKey: ['case-audit', employerId] });
    qc.invalidateQueries({ queryKey: ['team-jaarverslag', employerId] });
  };
  // 403 = nog geen teamlid (nog geen registraties bij deze opdrachtgever).
  const teamJaarQuery = useQuery({
    queryKey: teamJaarKey(employerId, jaar),
    queryFn: () => getTeamJaar(employerId, jaar),
    enabled: !!employerId && !!ctx && tab === 'audit',
    retry: false,
  });
  const teamJaar = teamJaarQuery.data;
  const alleenIk = !teamJaar || teamJaar.deelnemers.every((x) => x.is_ik);
  const ikInTeam = teamJaar?.deelnemers.find((x) => x.is_ik);

  const opslaanCorrectie = useMutation({
    mutationFn: () => {
      const { is_nieuw_incident, ...rest } = bewerk!.waarde;
      return corrigeer(bewerk!.id, is_nieuw_incident ? rest : { interventies: rest.interventies });
    },
    onSuccess: () => {
      toast.success('Correctie opgeslagen');
      setBewerk(null);
      verversen();
    },
    onError: foutmelding,
  });

  const samenvoegen = useMutation({
    mutationFn: (v: { id: string; doel: string }) => voegSamen(v.id, v.doel),
    onSuccess: () => {
      toast.success('Casussen samengevoegd');
      setBewerk(null);
      verversen();
    },
    onError: foutmelding,
  });

  const uitsluiten = useMutation({
    mutationFn: (v: { id: string; reden: string | null }) => zetUitsluiting(v.id, v.reden),
    onSuccess: (_, v) => {
      toast.success(v.reden ? 'Casus buiten het jaarverslag gehouden' : 'Casus telt weer mee');
      setBewerk(null);
      verversen();
    },
    onError: foutmelding,
  });

  const verificatie = useMutation({
    mutationFn: (aan: boolean) => zetVerificatie(employerId, jaar, aan),
    onSuccess: (_, aan) => {
      toast.success(aan ? 'Je data is geverifieerd' : 'Verificatie ingetrokken');
      verversen();
    },
    onError: foutmelding,
  });

  if (contextQuery.isLoading) return <div className="py-16 text-center text-gray-500">Laden…</div>;
  if (contextQuery.isError || !ctx) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <p className="text-gray-700">{(contextQuery.error as any)?.response?.data?.message || 'Case-registratie is niet beschikbaar.'}</p>
      </div>
    );
  }

  const isVtp = ctx.discipline === 'VTP';
  const jaren = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);
  const p = audit?.preview;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Case-registratie & jaarverslag</h1>
        <p className="text-sm text-gray-600 max-w-3xl">
          {ctx.discipline_label}. Je legt per gesprek alleen vaste kenmerken vast, nooit tekst. De werkgever ziet niets tot je het jaarverslag
          vrijgeeft, en dan alleen groepen van minimaal {ctx.min_groep} casussen.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-sm">
          <span className="text-gray-700">Opdrachtgever</span>
          <select className="mt-1 w-64 max-w-full rounded-md border-gray-300" value={employerId} onChange={(e) => setEmployerId(e.target.value)}>
            <option value="">Kies…</option>
            {ctx.opdrachtgevers.map((o) => <option key={o.id} value={o.id}>{o.naam}</option>)}
          </select>
        </label>
        <div className="flex rounded-md border border-gray-300 overflow-hidden text-sm" role="tablist">
          {(['registreren', 'audit', 'team'] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} className={`px-3 py-2 ${tab === t ? 'bg-[#0B1F3A] text-white' : 'bg-white text-gray-700'}`}>
              {t === 'registreren' ? 'Registreren' : t === 'audit' ? 'Jaarverslag controleren & releasen' : 'Team'}
            </button>
          ))}
        </div>
      </div>

      {!employerId ? (
        <p className="text-sm text-gray-500">Kies eerst een opdrachtgever.</p>
      ) : tab === 'team' ? (
        <div className="space-y-4">
          <label className="text-sm">
            <span className="text-gray-700 mr-2">Kalenderjaar</span>
            <select className="rounded-md border-gray-300" value={jaar} onChange={(e) => setJaar(Number(e.target.value))}>
              {jaren.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
          </label>
          <TeamJaarverslagPaneel employerId={employerId} jaar={jaar} renderPreview={(p) => <Preview p={p} />} />
        </div>
      ) : tab === 'registreren' ? (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3 max-w-3xl text-sm text-gray-700">
          <h2 className="font-semibold text-gray-900">Gesprekken registreren</h2>
          <p>
            Een gesprek registreer je via het gespreksverslag (migratie 176). Daarin leg je de kenmerken voor het jaarverslag vast (aard, rolverhouding,
            interventies), samen met de kwalitatieve organisatie-update (Vak B), de persoonlijke notities voor de medewerker (Vak A) en bij gesprek 1, 3 en 5
            de management-update. Een losse registratie zonder verslag bestaat niet meer.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Afspraak in de agenda (zelfboeking of door jou ingepland): open <strong>Verslag</strong> bij de afspraak.</li>
            <li>Gesprek zonder afspraak op de werkvloer: registreer een <strong>ad-hoc melding</strong>; de medewerker bevestigt per SMS.</li>
          </ul>
          <div className="flex flex-wrap gap-2 pt-1">
            <a href="/professional/agenda"><Button variant="outline">Naar de agenda</Button></a>
            <a href="/professional/adhoc-melding"><Button>Ad-hoc melding registreren</Button></a>
          </div>
          <p className="text-xs text-gray-500">Corrigeren, samenvoegen en uitsluiten doe je onder ‘Jaarverslag controleren & releasen’.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm">
              <span className="text-gray-700 mr-2">Kalenderjaar</span>
              <select className="rounded-md border-gray-300" value={jaar} onChange={(e) => setJaar(Number(e.target.value))}>
                {jaren.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            </label>
            {audit?.vergrendeld && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-medium">
                <Lock className="w-3 h-3" aria-hidden /> Vrijgegeven op {audit.vrijgegeven_op ? datumLabel(audit.vrijgegeven_op) : ''}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start">
            <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 px-3 font-medium">Casus</th>
                    <th className="py-2 px-3 font-medium">Datum</th>
                    <th className="py-2 px-3 font-medium">Kenmerken</th>
                    <th className="py-2 px-3 font-medium">Locatie / afdeling</th>
                    <th className="py-2 px-3 font-medium">Interventies</th>
                    <th className="py-2 px-3" />
                  </tr>
                </thead>
                <tbody>
                  {auditQuery.isLoading && <tr><td colSpan={6} className="py-10 text-center text-gray-500">Laden…</td></tr>}
                  {audit && audit.registraties.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-gray-500">Geen registraties in {jaar}.</td></tr>}
                  {audit?.registraties.map((r: Registratie) => {
                    const loc = structuur?.locaties.find((l) => l.id === r.location_id)?.naam;
                    const afd = structuur?.afdelingen.find((a) => a.id === r.department_id)?.naam;
                    const inBewerking = bewerk?.id === r.id;
                    return (
                      <React.Fragment key={r.id}>
                        <tr className="border-b border-gray-100 align-top">
                          <td className="py-2 px-3 whitespace-nowrap">
                            <span className="font-mono text-xs">{r.case_code}</span>
                            <span className={`ml-1 rounded px-1.5 py-0.5 text-[11px] ${r.is_nieuw_incident ? 'bg-blue-50 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>{r.is_nieuw_incident ? 'nieuw' : 'vervolg'}</span>
                            {r.case_uitgesloten_reden && (
                              <span className="ml-1 rounded px-1.5 py-0.5 text-[11px] bg-amber-50 text-amber-800">
                                buiten verslag: {labelVan(ctx.codes.uitsluit_redenen, r.case_uitgesloten_reden)}
                              </span>
                            )}
                            {r.overgenomen_van_collega && <span className="ml-1 rounded px-1.5 py-0.5 text-[11px] bg-purple-50 text-purple-800">van collega</span>}
                            {/* Ad-hoc werkvloergesprek (migratie 173): telt pas mee na SMS-bevestiging */}
                            {r.verificatie === 'PENDING_VERIFICATION' && (
                              <span className="ml-1 rounded px-1.5 py-0.5 text-[11px] bg-amber-50 text-amber-800" title="Telt mee zodra de medewerker het gesprek via de SMS bevestigt">wacht op bevestiging</span>
                            )}
                            {r.verificatie === 'REJECTED' && (
                              <span className="ml-1 rounded px-1.5 py-0.5 text-[11px] bg-red-50 text-red-800" title="Afgewezen door de medewerker of ingetrokken; telt niet mee">niet bevestigd</span>
                            )}
                            {r.case_jaar !== jaar && <div className="text-xs text-gray-500">casus uit {r.case_jaar}</div>}
                            {r.koppeling_label && <div className="text-xs text-gray-500">{r.koppeling_label}</div>}
                          </td>
                          <td className="py-2 px-3 whitespace-nowrap">{datumLabel(r.gesprek_datum)}</td>
                          <td className="py-2 px-3 text-xs">
                            {r.is_nieuw_incident ? r.categorieen.map((c) => labelVan(ctx.codes.categorieen, c)).join(', ') : <span className="text-gray-400">erft van casus</span>}
                            {r.rolverhouding && <div className="text-gray-500">{labelVan(ctx.codes.rolverhoudingen, r.rolverhouding)}</div>}
                            {r.geslacht_veroorzaker && (
                              <div className="text-gray-500">Beschuldigde: {labelVan(ctx.codes.geslachten_veroorzaker, r.geslacht_veroorzaker)}</div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-xs">{[loc, afd].filter(Boolean).join(' – ') || <span className="text-gray-400">niet bekend</span>}</td>
                          <td className="py-2 px-3 text-xs">{r.interventies.map((c) => labelVan(ctx.codes.interventies, c)).join(', ') || '—'}</td>
                          <td className="py-2 px-3 text-right">
                            {!audit.vergrendeld && (
                              <button
                                type="button"
                                title="Corrigeren"
                                className="text-gray-500 hover:text-gray-900"
                                onClick={() =>
                                  setBewerk(
                                    inBewerking
                                      ? null
                                      : {
                                          id: r.id,
                                          waarde: {
                                            is_nieuw_incident: r.is_nieuw_incident,
                                            categorieen: r.categorieen,
                                            rolverhouding: r.rolverhouding,
                                            geslacht_veroorzaker: r.geslacht_veroorzaker ?? null,
                                            location_id: r.location_id,
                                            department_id: r.department_id,
                                            interventies: r.interventies,
                                            overgenomen_van_collega: !!r.overgenomen_van_collega,
                                          },
                                        }
                                  )
                                }
                              >
                                {inBewerking ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                              </button>
                            )}
                          </td>
                        </tr>
                        {inBewerking && bewerk && (
                          <tr className="border-b border-gray-100 bg-gray-50">
                            <td colSpan={6} className="p-4 space-y-4">
                              {bewerk.waarde.is_nieuw_incident && (
                                <>
                                  <CheckboxLijst legend="Type melding" opties={ctx.codes.categorieen} waarde={bewerk.waarde.categorieen ?? []} onChange={(v) => setBewerk({ ...bewerk, waarde: { ...bewerk.waarde, categorieen: v } })} />
                                  {isVtp && <RolKeuze opties={ctx.codes.rolverhoudingen} waarde={bewerk.waarde.rolverhouding ?? null} onChange={(v) => setBewerk({ ...bewerk, waarde: { ...bewerk.waarde, rolverhouding: v } })} />}
                                  {isVtp && (
                                    <GeslachtVeroorzakerKeuze
                                      opties={ctx.codes.geslachten_veroorzaker}
                                      waarde={bewerk.waarde.geslacht_veroorzaker ?? null}
                                      onChange={(v) => setBewerk({ ...bewerk, waarde: { ...bewerk.waarde, geslacht_veroorzaker: v } })}
                                    />
                                  )}
                                  <AfdelingKeuze
                                    structuur={structuur}
                                    locatie={bewerk.waarde.location_id ?? ''}
                                    afdeling={bewerk.waarde.department_id ?? ''}
                                    onChange={(l, a) => setBewerk({ ...bewerk, waarde: { ...bewerk.waarde, location_id: l || null, department_id: a || null } })}
                                  />
                                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="rounded border-gray-300"
                                      checked={!!bewerk.waarde.overgenomen_van_collega}
                                      onChange={(e) => setBewerk({ ...bewerk, waarde: { ...bewerk.waarde, overgenomen_van_collega: e.target.checked } })}
                                    />
                                    Eerder door een collega geregistreerd
                                  </label>
                                </>
                              )}
                              <CheckboxLijst legend="Interventies" opties={ctx.codes.interventies} waarde={bewerk.waarde.interventies ?? []} onChange={(v) => setBewerk({ ...bewerk, waarde: { ...bewerk.waarde, interventies: v } })} />
                              <Button size="sm" onClick={() => opslaanCorrectie.mutate()} loading={opslaanCorrectie.isPending}>Correctie opslaan</Button>
                              {r.is_nieuw_incident && (
                                <div className="mt-4 grid gap-3 border-t border-gray-100 pt-3 sm:grid-cols-2">
                                  <div>
                                    <p className="text-sm font-medium text-gray-800">Samenvoegen</p>
                                    <p className="text-xs text-gray-500 mb-1">Was dit eigenlijk een vervolggesprek? Deze casus gaat dan op in de gekozen casus.</p>
                                    <div className="flex gap-2">
                                      <select className="flex-1 rounded-md border-gray-300 text-sm" value={samenDoel} onChange={(e) => setSamenDoel(e.target.value)}>
                                        <option value="">Kies casus…</option>
                                        {audit.registraties
                                          .filter((x) => x.is_nieuw_incident && x.id !== r.id && !x.case_uitgesloten_reden)
                                          .map((x) => (
                                            <option key={x.id} value={x.id}>{x.case_code} · {datumLabel(x.gesprek_datum)}</option>
                                          ))}
                                      </select>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={!samenDoel || !!r.case_uitgesloten_reden}
                                        loading={samenvoegen.isPending}
                                        onClick={() => {
                                          if (window.confirm(`${r.case_code} opheffen in de gekozen casus? Dit telt daarna als vervolggesprek.`)) samenvoegen.mutate({ id: r.id, doel: samenDoel });
                                        }}
                                      >
                                        Samenvoegen
                                      </Button>
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-800">Buiten het jaarverslag</p>
                                    <p className="text-xs text-gray-500 mb-1">Geldt voor alle gesprekken van deze casus.</p>
                                    {r.case_uitgesloten_reden ? (
                                      <Button size="sm" variant="outline" loading={uitsluiten.isPending} onClick={() => uitsluiten.mutate({ id: r.id, reden: null })}>
                                        Weer laten meetellen
                                      </Button>
                                    ) : (
                                      <div className="flex gap-2">
                                        <select className="flex-1 rounded-md border-gray-300 text-sm" value={uitsluitReden} onChange={(e) => setUitsluitReden(e.target.value)}>
                                          <option value="">Kies reden…</option>
                                          {ctx.codes.uitsluit_redenen.map((o) => (
                                            <option key={o.code} value={o.code}>{o.label}</option>
                                          ))}
                                        </select>
                                        <Button size="sm" variant="outline" disabled={!uitsluitReden} loading={uitsluiten.isPending} onClick={() => uitsluiten.mutate({ id: r.id, reden: uitsluitReden })}>
                                          Uitsluiten
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <aside className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900">Preview voor de werkgever</h3>
                <p className="text-xs text-gray-500">
                  {p ? `${p.aantal_casussen ?? `Minder dan ${p.min_groep}`} casussen · ${p.aantal_gesprekken} gesprekken.` : ''} Groepen kleiner dan {ctx.min_groep} zijn al samengevoegd.
                </p>
              </div>
              {p && <Preview p={p} />}
              {audit && !audit.vergrendeld && (
                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <p className="flex gap-1.5 text-sm text-gray-700">
                    <Users className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
                    {alleenIk
                      ? 'Het jaarverslag stel je samen op het tabblad Team: daar schrijf je de hoofdstukken, verifieer je je data en geef je het verslag als coördinator vrij.'
                      : `Meer collega's registreren voor deze opdrachtgever in ${jaar}. Je eigen cijfers gaan niet los naar de werkgever: de coördinator geeft één geconsolideerd jaarverslag vrij. Deze preview toont alleen jouw deel.`}
                  </p>
                  {ikInTeam && (
                    <Button
                      className="w-full"
                      variant={ikInTeam.geverifieerd ? 'outline' : 'primary'}
                      loading={verificatie.isPending}
                      onClick={() => verificatie.mutate(!ikInTeam.geverifieerd)}
                    >
                      {ikInTeam.geverifieerd ? 'Verificatie intrekken' : '✓ Mijn data is geverifieerd'}
                    </Button>
                  )}
                  <Button className="w-full" variant="outline" onClick={() => setTab('team')}>Naar het teamverslag</Button>
                </div>
              )}
              {audit && audit.organisatie_signalen.length > 0 && (
                <div className="border-t border-gray-100 pt-4 space-y-2">
                  <h3 className="text-sm font-semibold text-gray-900">Organisatiesignalen (Vak B en werkvloeradvies) — alleen voor jou</h3>
                  <p className="text-xs text-gray-500">
                    Bronmateriaal voor hoofdstuk 5. De werkgever ziet alleen wat het team zelf in het teamverslag onder 5.1 en 5.5 schrijft
                    {isVtp ? '; signalen uit een casus met discriminatie zijn gemarkeerd voor 5.1 ‘De aard van discriminatie’' : ''}.
                  </p>
                  <ul className="max-h-64 overflow-y-auto space-y-2">
                    {audit.organisatie_signalen.map((sig) => (
                      <li key={sig.id} className="rounded border border-gray-200 p-2 text-xs">
                        <p className="text-gray-500 mb-1">
                          {datumLabel(sig.datum)} · {sig.case_code} · gesprek {sig.volgnummer}
                          {isVtp && sig.discriminatie && <span className="ml-1 rounded bg-purple-50 px-1.5 py-0.5 text-[11px] text-purple-800">discriminatie</span>}
                        </p>
                        {sig.tekst && <div className="prose prose-xs max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sig.tekst) }} />}
                        {sig.advies && (
                          <p className="mt-1 whitespace-pre-wrap text-gray-800">
                            <span className="font-medium">Werkvloeradvies:</span> {sig.advies}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalJaarverslagPage;
